import React, { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { GraduationCap, Send, CheckCircle, Users, MapPin, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  requester_name: '',
  requester_email: '',
  requester_phone: '',
  organization: '',
  theme: '',
  number_of_participants: 10,
  location: '',
  requested_date: '',
  description: ''
};

export default function TrainingPage() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.description.length > 500) {
      toast.error('La description ne doit pas dépasser 500 caractères');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/training-requests`, {
        ...formData,
        number_of_participants: parseInt(formData.number_of_participants, 10)
      });
      setSubmitted(true);
      toast.success('Demande envoyée avec succès !');
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setLoading(false);
    }
  };

  const charsLeft = 500 - formData.description.length;

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster />
      <Navigation />

      <section className="section-padding bg-white" data-testid="training-page">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#4A6B48]/10 mb-4">
              <GraduationCap className="text-[#4A6B48]" size={40} />
            </div>
            <h1 
              className="text-4xl sm:text-5xl font-bold text-[#2E332D] mb-4"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
              data-testid="training-title"
            >
              Demande de Formation FPT
            </h1>
            <p className="text-base text-[#5C665A] max-w-2xl mx-auto leading-relaxed">
              <strong>&quot;Former Pour Transformer&quot;</strong> — Notre approche de sensibilisation conscientisante 
              inspirée de Paulo Freire. Soumettez votre demande de formation pour votre organisation ou communauté.
            </p>
          </div>

          {submitted ? (
            <div className="card max-w-2xl mx-auto text-center py-12" data-testid="training-success">
              <CheckCircle className="text-[#4A6B48] mx-auto mb-4" size={64} />
              <h2 className="text-2xl font-bold text-[#2E332D] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Demande envoyée !
              </h2>
              <p className="text-[#5C665A] mb-6">
                Merci pour votre demande de formation. Notre équipe va l&apos;étudier et vous contactera prochainement
                pour discuter des détails et de la faisabilité.
              </p>
              <button
                onClick={() => { setSubmitted(false); setFormData(EMPTY_FORM); }}
                className="btn-primary"
              >
                Faire une autre demande
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="card max-w-2xl mx-auto" data-testid="training-form">
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-[#2E332D] mb-2">Nom complet *</label>
                    <input
                      type="text" name="requester_name" required
                      value={formData.requester_name} onChange={handleChange}
                      className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                      data-testid="training-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#2E332D] mb-2">Email *</label>
                    <input
                      type="email" name="requester_email" required
                      value={formData.requester_email} onChange={handleChange}
                      className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                      data-testid="training-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#2E332D] mb-2">Téléphone</label>
                    <input
                      type="tel" name="requester_phone"
                      value={formData.requester_phone} onChange={handleChange}
                      placeholder="+243 XXX XXX XXX"
                      className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#2E332D] mb-2">Organisation</label>
                    <input
                      type="text" name="organization"
                      value={formData.organization} onChange={handleChange}
                      placeholder="ONG, école, communauté..."
                      className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-2 flex items-center gap-2">
                    <FileText size={16} /> Thème de la formation *
                  </label>
                  <input
                    type="text" name="theme" required
                    value={formData.theme} onChange={handleChange}
                    placeholder="Ex: Agroforesterie, Citoyenneté participative, Santé communautaire..."
                    className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                    data-testid="training-theme-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-[#2E332D] mb-2 flex items-center gap-1">
                      <Users size={14} /> Nb participants *
                    </label>
                    <input
                      type="number" name="number_of_participants" required min="1"
                      value={formData.number_of_participants} onChange={handleChange}
                      className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                      data-testid="training-participants-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#2E332D] mb-2 flex items-center gap-1">
                      <MapPin size={14} /> Lieu *
                    </label>
                    <input
                      type="text" name="location" required
                      value={formData.location} onChange={handleChange}
                      placeholder="Ville, Province"
                      className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                      data-testid="training-location-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-[#2E332D] mb-2 flex items-center gap-1">
                      <Calendar size={14} /> Date souhaitée *
                    </label>
                    <input
                      type="date" name="requested_date" required
                      value={formData.requested_date} onChange={handleChange}
                      className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                      data-testid="training-date-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-[#2E332D] mb-2">
                    Description de la formation * 
                    <span className={`ml-2 text-xs font-normal ${charsLeft < 50 ? 'text-red-500' : 'text-[#5C665A]'}`}>
                      ({charsLeft}/500 caractères)
                    </span>
                  </label>
                  <textarea
                    name="description" required rows={5} maxLength={500}
                    value={formData.description} onChange={handleChange}
                    placeholder="Décrivez vos besoins, objectifs et contexte..."
                    className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                    data-testid="training-description-input"
                  />
                </div>

                <button
                  type="submit" disabled={loading}
                  className="w-full btn-primary py-3 inline-flex items-center justify-center disabled:opacity-50"
                  data-testid="submit-training-button"
                >
                  <Send size={18} className="mr-2" />
                  {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
